'use client';
import React, { useState, useMemo } from 'react';
import StockSummaryCards from './StockSummaryCards';
import StockTable from './StockTable';
import StockHealthChart from './StockHealthChart';
import ReorderAlertPanel from './ReorderAlertPanel';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

export interface StockPosition {
  id: string;
  itemCode: string;
  itemName: string;
  category: string;
  site: string;
  onHand: number;
  reserved: number;
  available: number;
  reorderLevel: number;
  safetyStock: number;
  uom: string;
  unitCost: number;
  lastReceipt: string;
  lastIssuance: string;
  health: 'HEALTHY' | 'LOW' | 'CRITICAL' | 'OUT_OF_STOCK';
}

export const STOCK_DATA: StockPosition[] = [
  { id: 'stk-001', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', category: 'CONSUMABLE', site: 'Warehouse B', onHand: 12, reserved: 8, available: 4, reorderLevel: 50, safetyStock: 20, uom: 'L', unitCost: 4.80, lastReceipt: '10 Jul 2026', lastIssuance: '25 Jul 2026', health: 'CRITICAL' },
  { id: 'stk-002', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', category: 'PPE', site: 'Site Alpha', onHand: 3, reserved: 3, available: 0, reorderLevel: 10, safetyStock: 5, uom: 'EA', unitCost: 142.00, lastReceipt: '15 Jun 2026', lastIssuance: '20 Jul 2026', health: 'OUT_OF_STOCK' },
  { id: 'stk-003', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', category: 'RAW_MATERIAL', site: 'Site Beta', onHand: 480, reserved: 120, available: 360, reorderLevel: 100, safetyStock: 50, uom: 'M', unitCost: 1.35, lastReceipt: '22 Jul 2026', lastIssuance: '27 Jul 2026', health: 'HEALTHY' },
  { id: 'stk-004', itemCode: 'PPE-GLOVE-L', itemName: 'Nitrile Gloves (Large)', category: 'PPE', site: 'Site Gamma', onHand: 24, reserved: 0, available: 24, reorderLevel: 100, safetyStock: 50, uom: 'PR', unitCost: 0.95, lastReceipt: '18 Jul 2026', lastIssuance: '26 Jul 2026', health: 'LOW' },
  { id: 'stk-005', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', category: 'SPARE_PART', site: 'Warehouse B', onHand: 4, reserved: 2, available: 2, reorderLevel: 20, safetyStock: 8, uom: 'EA', unitCost: 18.40, lastReceipt: '05 Jul 2026', lastIssuance: '18 Jul 2026', health: 'CRITICAL' },
  { id: 'stk-006', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade', category: 'RAW_MATERIAL', site: 'Site Alpha', onHand: 820, reserved: 400, available: 420, reorderLevel: 200, safetyStock: 80, uom: 'BAG', unitCost: 7.20, lastReceipt: '26 Jul 2026', lastIssuance: '28 Jul 2026', health: 'HEALTHY' },
  { id: 'stk-007', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', category: 'SAFETY_ITEM', site: 'Site Beta', onHand: 12, reserved: 0, available: 12, reorderLevel: 8, safetyStock: 4, uom: 'EA', unitCost: 89.50, lastReceipt: '01 Jul 2026', lastIssuance: '15 Jul 2026', health: 'HEALTHY' },
  { id: 'stk-008', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', category: 'TOOL', site: 'Warehouse B', onHand: 7, reserved: 2, available: 5, reorderLevel: 5, safetyStock: 2, uom: 'EA', unitCost: 68.00, lastReceipt: '20 Jun 2026', lastIssuance: '12 Jul 2026', health: 'HEALTHY' },
  { id: 'stk-009', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', category: 'RAW_MATERIAL', site: 'Site Alpha', onHand: 18, reserved: 10, available: 8, reorderLevel: 5, safetyStock: 2, uom: 'MT', unitCost: 680.00, lastReceipt: '24 Jul 2026', lastIssuance: '28 Jul 2026', health: 'HEALTHY' },
  { id: 'stk-010', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', category: 'PPE', site: 'Site Gamma', onHand: 18, reserved: 5, available: 13, reorderLevel: 20, safetyStock: 10, uom: 'EA', unitCost: 12.50, lastReceipt: '10 Jul 2026', lastIssuance: '22 Jul 2026', health: 'LOW' },
  { id: 'stk-011', itemCode: 'SEAL-ORING-32', itemName: 'O-Ring NBR 32mm ID', category: 'SPARE_PART', site: 'Warehouse B', onHand: 148, reserved: 0, available: 148, reorderLevel: 50, safetyStock: 20, uom: 'EA', unitCost: 0.45, lastReceipt: '28 Jun 2026', lastIssuance: '14 Jul 2026', health: 'HEALTHY' },
  { id: 'stk-012', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', category: 'CONSUMABLE', site: 'Site Beta', onHand: 32, reserved: 10, available: 22, reorderLevel: 25, safetyStock: 10, uom: 'KG', unitCost: 14.20, lastReceipt: '20 Jul 2026', lastIssuance: '24 Jul 2026', health: 'HEALTHY' },
];

const SITES = ['All Sites', 'Site Alpha', 'Site Beta', 'Site Gamma', 'Warehouse B'];

export default function StockPositionsContent() {
  const [siteFilter, setSiteFilter] = useState('All Sites');
  const [healthFilter, setHealthFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return STOCK_DATA.filter((s) => {
      const matchSite = siteFilter === 'All Sites' || s.site === siteFilter;
      const matchHealth = healthFilter === 'ALL' || s.health === healthFilter;
      const matchSearch = search === '' ||
        s.itemCode.toLowerCase().includes(search.toLowerCase()) ||
        s.itemName.toLowerCase().includes(search.toLowerCase());
      return matchSite && matchHealth && matchSearch;
    });
  }, [siteFilter, healthFilter, search]);

  const totalValue = filtered.reduce((acc, s) => acc + s.onHand * s.unitCost, 0);
  const totalReserved = filtered.reduce((acc, s) => acc + s.reserved, 0);
  const belowReorder = filtered.filter((s) => s.available < s.reorderLevel).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-600 text-foreground">Stock Positions</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Real-time on-hand, reserved, and available quantities across all sites
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.success('Stock count sheet exported.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors bg-card"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export
          </button>
          <button
            onClick={() => toast.info('Stock count screen — coming soon.')}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95"
          >
            <Icon name="ClipboardDocumentCheckIcon" size={16} />
            Start Stock Count
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <StockSummaryCards
        totalSkus={filtered.length}
        totalValue={totalValue}
        totalReserved={totalReserved}
        belowReorder={belowReorder}
      />

      {/* Main content */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        <div className="xl:col-span-3 space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by item code or name…"
                className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm bg-card outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all"
              />
            </div>
            {/* Site filter */}
            <select
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              className="px-3 py-2.5 border border-border rounded-lg text-sm bg-card outline-none focus:ring-2 focus:ring-ring/30 transition-all"
            >
              {SITES.map((s) => (
                <option key={`site-opt-${s}`} value={s}>{s}</option>
              ))}
            </select>
            {/* Health filter */}
            <div className="flex bg-muted p-1 rounded-lg shrink-0">
              {([
                { id: 'ALL', label: 'All' },
                { id: 'HEALTHY', label: 'Healthy' },
                { id: 'LOW', label: 'Low' },
                { id: 'CRITICAL', label: 'Critical' },
                { id: 'OUT_OF_STOCK', label: 'Out' },
              ]).map((h) => (
                <button
                  key={`health-fil-${h.id}`}
                  onClick={() => setHealthFilter(h.id)}
                  className={`px-3 py-1.5 text-xs font-500 rounded-md transition-all whitespace-nowrap ${
                    healthFilter === h.id ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>

          <StockTable items={filtered} />
        </div>

        <div className="space-y-4">
          <StockHealthChart data={STOCK_DATA} />
          <ReorderAlertPanel items={STOCK_DATA.filter((s) => s.health === 'CRITICAL' || s.health === 'OUT_OF_STOCK')} />
        </div>
      </div>
    </div>
  );
}